import React, { useState } from "react";
import { Card, Flex, Text, toast, FileUpload, ErrorMessage } from "@jsq/ds/core";
import { Form, FormRadioButton, FormRadioGroup, FormSelect } from "@jsq/ds/forms";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { CountrySelectionDropDown } from "../../../../../common/components/CountrySelectionDropDown";
import useUploadFilesToS3 from "../../../../../common/hooks/uploadFilesToS3/useUploadFilesToS3";
import { useGetCountryCodesQuery } from "../../../../../graphql/supergraph.index";
import { getCountryValueFromId, getIdFromCountry } from "../../../../../utils/country_utils";
import { clientContext } from "../../../../../utils/graphql_utils";
import { SharedAddressInput } from "../../../../SharedAddressInput";
import { DocumentTracker } from "../../../components/DocumentTracker";
import { FormIntlTaxIdInput, AccountType } from "../../../components/FormIntlTaxIdInput";
import { FormStepFooter } from "../../../components/FormStepFooter";
import { PayeeDetailsBase } from "../../../components/PayeeDetailsBase";
import { PayeeActionTypes, PayeeStepActionTypes } from "../../../Context/constants";
import {
  useBasePayeeReducerContext,
  usePayeeDispatchContext,
  usePayeeWorkflowDispatchContext,
} from "../../../Context/PayeeContext";
import {
  stepColumnContainerStyles,
  stepContainerStyles,
  stepWithDocumentTrackerStyles,
} from "../../../styles_vanilla.css";
import { TaxDocumentType } from "../../types";
import {
  TaxFormType,
  TAX_FORM_DISPLAY_NAMES,
  DOCUMENT_TYPE_TO_FORM_TYPE,
  FORM_TYPE_TO_DOCUMENT_TYPE,
} from "./constants";
import { vendorAddressStyles } from "./styles_vanilla.css";
import { payeeDetailsValidationSchema, VendorPayeeDetailsFields } from "./types";
import { shouldDispatchPayeeDetails } from "./utils";

const getDocumentTypeFromFormType = (formType: TaxFormType): TaxDocumentType => {
  return FORM_TYPE_TO_DOCUMENT_TYPE[formType];
};

const getFormTypeFromDocumentType = (documentType: TaxDocumentType): TaxFormType => {
  return DOCUMENT_TYPE_TO_FORM_TYPE[documentType];
};

const createTaxDocument = (file: File, s3ObjectKey: string, documentType: TaxDocumentType) => ({
  name: file.name,
  size: file.size,
  s3ObjectKey,
  localUrl: URL.createObjectURL(file),
  file,
  contentType: file.type,
  documentType,
});

export const VendorPayeeDetailsStep: React.FC = () => {
  const stepDispatch = usePayeeWorkflowDispatchContext();
  const payeeDispatch = usePayeeDispatchContext();

  const { payeeName, payeeAddress, form1099Required, taxDocuments, taxCountryId, taxId } =
    useBasePayeeReducerContext();
  const { invoices } = useBasePayeeReducerContext();

  // Query country data to reconstruct the combined format
  const { data: countryData } = useGetCountryCodesQuery({
    context: clientContext(),
  });

  // State for tax form functionality - initialize from context
  const [selectedTaxFormType, setSelectedTaxFormType] = useState<TaxFormType | undefined>(
    taxDocuments && taxDocuments.length > 0
      ? getFormTypeFromDocumentType(taxDocuments[0].documentType)
      : undefined,
  );
  const [taxFormFile, setTaxFormFile] = useState<File | null>(
    taxDocuments && taxDocuments.length > 0 ? taxDocuments[0].file : null,
  );
  const [taxFormError, setTaxFormError] = useState<string | null>(null);

  // S3 upload hook
  const { uploadFilesToS3 } = useUploadFilesToS3();

  return (
    <div className={stepContainerStyles}>
      <Form
        initialValues={{
          payeeName: payeeName || "",
          country: payeeAddress?.countryId,
          state: payeeAddress?.stateId,
          province: payeeAddress?.region,
          streetLine1: payeeAddress?.street,
          streetLine2: payeeAddress?.street2,
          city: payeeAddress?.city,
          zip: payeeAddress?.postalCode,
          form1099Required: form1099Required,
          taxCountryId: getCountryValueFromId(taxCountryId, countryData?.countries),
          taxId: taxId || "",
          taxFormType:
            taxDocuments && taxDocuments.length > 0
              ? getFormTypeFromDocumentType(taxDocuments[0].documentType)
              : selectedTaxFormType,
        }}
        onSubmit={async (values: VendorPayeeDetailsFields) => {
          await handleTaxDocumentSubmission();

          const dispatchPayeeDetails = shouldDispatchPayeeDetails(
            values,
            payeeName,
            payeeAddress,
            form1099Required,
            taxDocuments,
            taxCountryId,
            taxId,
          );
          if (dispatchPayeeDetails) {
            payeeDispatch({
              type: PayeeActionTypes.SET_PAYEE_DETAILS_VENDOR,
              payload: {
                payeeName: values.payeeName,
                payeeAddress: {
                  street: values.streetLine1,
                  street2: values.streetLine2,
                  city: values.city,
                  postalCode: values.zip,
                  stateId: values.state,
                  countryId: values.country,
                  region: values.province,
                },
                form1099Required: values.form1099Required,
                taxCountryId: getIdFromCountry(values.taxCountryId as string),
                taxId: values.taxId,
              },
            });
          }
          stepDispatch({ type: PayeeStepActionTypes.INCREMENT_STEP });
        }}
        yupValidationSchema={payeeDetailsValidationSchema}
        submitOnlyChangedValues={false}
      >
        <ColumnContainer className={stepColumnContainerStyles}>
          <Column size={"fluid"}>
            <DocumentTracker
              documentName={invoices?.[0].name ?? ""}
              documentUrl={invoices?.[0].localUrl ?? ""}
            />
          </Column>
          <Column size={"fluid"} className={stepWithDocumentTrackerStyles}>
            <Card title="Payee Details">
              <Flex flexDirection="column" gap={12}>
                <PayeeDetailsBase payeeNameLabel="Vendor" />
                <Flex gap={2}>
                  <Text variant="strong-md">Vendor address</Text>
                  <Text variant="secondary-md">(optional)</Text>
                </Flex>
                <div className={vendorAddressStyles}>
                  <SharedAddressInput CountrySelect={CountrySelectionDropDown} />
                </div>

                {/* Tax ID Section */}
                <FormIntlTaxIdInput
                  taxIdFieldName="taxId"
                  taxCountryFieldName="taxCountryId"
                  accountType={AccountType.Entity}
                  optional={true}
                  skipValidation={false}
                  displayErrorMessage={true}
                />
                {/* Tax Form Section */}
                <Flex flexDirection="column" gap={6}>
                  <Flex gap={4}>
                    <Text variant="strong-md">Tax form</Text>
                    <Text variant="secondary-md">(optional)</Text>
                  </Flex>
                  <FormSelect
                    name="taxFormType"
                    placeholder="Select tax form type"
                    full
                    handleChange={(value: string | number | string[] | number[]) => {
                      const formType = value as TaxFormType | undefined;
                      setSelectedTaxFormType(formType);
                      if (!formType) {
                        setTaxFormFile(null);
                        setTaxFormError(null);
                      }
                    }}
                  >
                    <FormSelect.Section>
                      <FormSelect.Option value={TaxFormType.W9}>
                        {TAX_FORM_DISPLAY_NAMES[TaxFormType.W9]}
                      </FormSelect.Option>
                      <FormSelect.Option value={TaxFormType.W8}>
                        {TAX_FORM_DISPLAY_NAMES[TaxFormType.W8]}
                      </FormSelect.Option>
                    </FormSelect.Section>
                  </FormSelect>
                </Flex>

                {/* Conditional File Upload */}
                {selectedTaxFormType && (
                  <Flex flexDirection="column" gap={6}>
                    <Text variant="strong-md">Upload tax form</Text>
                    <Flex>
                      <FileUpload
                        isFullWidth
                        uploadMessage="Upload file"
                        acceptedFileTypes={[".jpg", ".png", ".pdf"]}
                        canAllowMultiple={false}
                        displayAcceptedFileTypes={true}
                        guidanceMessage=""
                        alwaysShowGuidance={false}
                        onFileSelected={(fileObjs) => {
                          if (fileObjs.length > 0 && fileObjs[0].status === "accepted") {
                            const file = fileObjs[0].file;
                            setTaxFormFile(file);
                            setTaxFormError(null);
                          }
                        }}
                        size="small"
                        showFileList={true}
                        initialFiles={
                          taxFormFile
                            ? [
                                {
                                  file: taxFormFile,
                                  status: "accepted",
                                  uuid: taxFormFile.name,
                                },
                              ]
                            : []
                        }
                        onDelete={() => {
                          setTaxFormFile(null);
                          setTaxFormError(null);
                        }}
                        data-testid="UploadTaxForm"
                      />
                    </Flex>
                    {taxFormError && (
                      <ErrorMessage style={{ color: "#DC2626" }}>{taxFormError}</ErrorMessage>
                    )}
                  </Flex>
                )}

                {/* 1099 Required Section */}
                <Flex flexDirection="column" gap={6}>
                  <Flex gap={4}>
                    <Text variant="strong-md">1099 Required?</Text>
                    <Text variant="secondary-md">(optional)</Text>
                  </Flex>
                  <FormRadioGroup name="form1099Required" displayErrorMessage>
                    <FormRadioButton value="yes">Yes</FormRadioButton>
                    <FormRadioButton value="no">No</FormRadioButton>
                  </FormRadioGroup>
                </Flex>
              </Flex>
            </Card>
          </Column>
        </ColumnContainer>
        <FormStepFooter
          onBack={() => {
            stepDispatch({ type: PayeeStepActionTypes.DECREMENT_STEP });
          }}
        />
      </Form>
    </div>
  );

  // Extract tax document handling logic
  async function handleTaxDocumentSubmission() {
    if (!selectedTaxFormType || !taxFormFile) {
      // Clear tax documents if no type or file is selected
      payeeDispatch({
        type: PayeeActionTypes.SET_TAX_DOCUMENTS,
        payload: { taxDocuments: [] },
      });
      return;
    }

    const existingTaxDoc = taxDocuments?.[0];
    const documentType = getDocumentTypeFromFormType(selectedTaxFormType);

    // If the file hasn't changed and document type is the same, don't re-upload
    if (
      existingTaxDoc &&
      existingTaxDoc.file === taxFormFile &&
      existingTaxDoc.documentType === documentType
    ) {
      return;
    }

    // If file changed, upload new file
    if (!existingTaxDoc || existingTaxDoc.file !== taxFormFile) {
      const uploadedFiles = await handleNewFileUpload();
      if (uploadedFiles.length > 0) {
        const taxDocument = createTaxDocument(
          taxFormFile,
          uploadedFiles[0].objectKey,
          documentType,
        );
        payeeDispatch({
          type: PayeeActionTypes.SET_TAX_DOCUMENTS,
          payload: { taxDocuments: [taxDocument] },
        });
      }
    } else if (existingTaxDoc && existingTaxDoc.documentType !== documentType) {
      // Update document type without re-uploading
      payeeDispatch({
        type: PayeeActionTypes.SET_TAX_DOCUMENTS,
        payload: {
          taxDocuments: [
            {
              ...existingTaxDoc,
              documentType,
            },
          ],
        },
      });
    }
  }

  async function handleNewFileUpload() {
    if (!taxFormFile) return [];

    try {
      return await uploadFilesToS3([taxFormFile]);
    } catch (error) {
      toast.error("Failed to upload tax form. Please try again.");
      throw error; // Re-throw to prevent form submission
    }
  }
};

export default VendorPayeeDetailsStep;
